import { Component, effect, inject, input, model, OnDestroy, output, signal, untracked } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { ButtonComponent, DialogComponent } from '@visio-ai/ui-kit'
import { Subscription, timeout } from 'rxjs'
import { PhaseFormField } from '../../../../_services/kanban/kanban.model'
import { PvtKanbanService } from '../../../../_services/kanban/kanban.service'

@Component({
  selector: 'app-phase-form',
  standalone: true,
  imports: [DialogComponent, ReactiveFormsModule, ButtonComponent],
  templateUrl: './phase-form.component.html',
  styleUrl: './phase-form.component.scss'
})
export class PhaseFormComponent implements OnDestroy {
  private pvtKanbanService = inject(PvtKanbanService)

  boardId = input.required<string>()
  cardId = input.required<string>()
  phaseId = input.required<string>()
  phaseName = input<string>('')
  isVisible = model<boolean>(false)

  onSubmit = output<void>()

  protected form: FormGroup = new FormGroup({})
  protected loading = signal(false)
  protected submitting = signal(false)
  protected formSchema = signal<PhaseFormField[]>([])
  private formRequestSub?: Subscription

  constructor() {
    effect(() => {
      const visible = this.isVisible()
      const phaseId = this.phaseId()
      const cardId = this.cardId()

      if (visible && phaseId && cardId) {
        untracked(() => this.initForm())
      }
    })
  }

  private initForm(): void {
    this.formRequestSub?.unsubscribe()
    this.loading.set(true)
    this.formSchema.set([])

    this.formRequestSub = this.pvtKanbanService
      .getPhaseFormSubmission(this.boardId(), this.cardId(), this.phaseId())
      .pipe(timeout(15000))
      .subscribe({
        next: response => {
          this.formSchema.set(response.formSchema)
          this.buildForm(response.formSchema)
          if (response.submission?.data) {
            this.populateForm(response.submission.data)
          }
          this.loading.set(false)
        },
        error: () => this.loading.set(false)
      })
  }

  private buildForm(schema: PhaseFormField[]): void {
    const controls: Record<string, FormControl> = {}
    for (const field of schema) {
      const validators = field.required ? [Validators.required] : []
      const defaultValue =
        field.type === 'boolean' ? false : field.type === 'multiselect' ? [] : null
      controls[field.id] = new FormControl(defaultValue, validators)
    }
    this.form = new FormGroup(controls)
  }

  private populateForm(data: Record<string, unknown>): void {
    for (const [key, value] of Object.entries(data)) {
      this.form.get(key)?.setValue(value ?? null)
    }
  }

  protected isOptionChecked(fieldId: string, value: string): boolean {
    const values = (this.form.get(fieldId)?.value as string[]) || []
    return values.includes(value)
  }

  protected onCheckboxChange(event: Event, fieldId: string): void {
    const checkbox = event.target as HTMLInputElement
    const control = this.form.get(fieldId)
    const currentValues = (control?.value as string[]) || []
    if (checkbox.checked) {
      control?.setValue([...currentValues, checkbox.value])
    } else {
      control?.setValue(currentValues.filter((v: string) => v !== checkbox.value))
    }
  }

  ngOnDestroy(): void {
    this.formRequestSub?.unsubscribe()
  }

  protected blurOnWheel(event: WheelEvent): void {
    (event.target as HTMLElement).blur()
  }

  protected submit(): void {
    if (this.form.invalid) return

    this.submitting.set(true)
    const data = this.form.getRawValue() as Record<string, unknown>

    this.pvtKanbanService
      .submitPhaseForm(this.boardId(), this.cardId(), this.phaseId(), data)
      .subscribe({
        next: () => {
          this.submitting.set(false)
          this.onSubmit.emit()
          this.isVisible.set(false)
        },
        error: err => {
          console.error('submitPhaseForm failed', err)
          this.submitting.set(false)
        }
      })
  }
}
